import { Injectable, inject, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

import { ApiResponse } from '../../_models/api-response.model';
import { RegisterRequest } from '../../_models/auth/register_request.model';
import { LoginRequest } from '../../_models/auth/login-request.model';
import { AuthResponse } from '../../_models/auth/auth-response.model.';
import { PasswordResetRequest } from '../../_models/auth/password-reset-request.model';
import { TokenValidationRequest } from '../../_models/auth/token-validation-request.model';
import { TokenValidationResponse } from '../../_models/auth/token-validation-response.model';
import { PasswordResetConfirmRequest } from '../../_models/auth/password-reset-confirm-request.model';
import { StorageService } from '../app/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService { 
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private apiUrl = environment.apiUrl + '/auth';
  private readonly TOKEN_KEY = 'auth_token';
  private readonly ROLES_KEY = 'user_roles';
  private loggedInSubject = new BehaviorSubject<boolean>(this.hasToken());

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  register(registerRequest: RegisterRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/register`, registerRequest);
  }

  login(loginRequest: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, loginRequest)
      .pipe(
        tap(response => {
          this.setToken(response.token);
          this.decodeAndStoreUserRoles(response.token);
          this.loggedInSubject.next(true);
        })
      );
  }

  logout(): void {
    this.storageService.removeItem(this.TOKEN_KEY);
    this.storageService.removeItem(this.ROLES_KEY);
    this.loggedInSubject.next(false);
  }

  verifyEmail(token: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/verify-email`, null, {
      params: { token }
    }).pipe(
      catchError(error => {
        if (error.status === 400 || error.status === 401) {
          throw new Error('Token de vérification invalide ou expiré.');
        } else if (error.status === 409) {
          throw new Error('Cette adresse email est déjà vérifiée.');
        }
        throw new Error('Une erreur est survenue lors de la vérification.');
      })
    );
  }

  resendVerification(email: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/resend-verification`, null, {
      params: { email }
    });
  }

  requestPasswordReset(email: string): Observable<ApiResponse<void>> {
    const request: PasswordResetRequest = { email };
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/password/reset-request`, request);
  }

  validatePasswordResetToken(token: string): Observable<ApiResponse<TokenValidationResponse>> {
    const request: TokenValidationRequest = { token };
    return this.http.post<ApiResponse<TokenValidationResponse>>(
      `${this.apiUrl}/password/validate-token`, 
      request
    );
  }

  resetPassword(token: string, newPassword: string): Observable<ApiResponse<void>> {
    const request: PasswordResetConfirmRequest = { token, newPassword };
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/password/reset`, request);
  }

  // Token management methods
  private hasToken(): boolean {
    if (!this.isBrowser()) return false;
    return !!this.storageService.getItem(this.TOKEN_KEY);
  }

  setToken(token: string): void {
    this.storageService.setItem(this.TOKEN_KEY, token, 24); // Token expires in 24 hours
  }

  getToken(): string | null {
    return this.storageService.getItem(this.TOKEN_KEY);
  }

  removeToken(): void {
    this.storageService.removeItem(this.TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp;
      const now = Math.floor(Date.now() / 1000);
      return expiry < now;
    } catch {
      return true;
    }
  }

  isLoggedIn(): boolean {
    if (!this.isBrowser()) return false;
    const token = this.getToken();
    return !!token && !this.isTokenExpired();
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.loggedInSubject.asObservable();
  }

  // Role management methods
  private decodeAndStoreUserRoles(token: string): void {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      //const roles = payload.roles || payload.authorities || [];
      //this.setRoles(roles);
      const roles = (payload.roles || payload.authorities || []).map((r: any) => r.authority);
      this.setRoles(roles);
    } catch (error) {
      console.error('Error decoding token:', error);
      this.setRoles([]);
    }
  }

  setRoles(roles: string[]): void {
    if (!roles || roles.length === 0) {
      this.storageService.removeItem(this.ROLES_KEY);
      return;
    }
    this.storageService.setSimpleItem(this.ROLES_KEY, roles);
  }

  getRoles(): string[] {
    return this.storageService.getSimpleItem(this.ROLES_KEY) || [];
  }

  clearRoles(): void {
    this.storageService.removeItem(this.ROLES_KEY);
  }

  isAdmin(): boolean {
    const roles = this.getRoles();
    return roles.includes('ROLE_ADMIN');
  }

  isUser(): boolean {
    const roles = this.getRoles();
    return roles.includes('ROLE_USER');
  }

  isAgent(): boolean {
    const roles = this.getRoles();
    return roles.includes('ROLE_AGENT');
  }

  getUserInfo(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }

   setUserProfileImage(urlProfilePicture: any){
    localStorage.setItem("urlProfilePicture", urlProfilePicture || './assets/images/no-user-profile-img.png');
  }
}